import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { AgGridReact } from '@ag-grid-community/react';
import { AllModules } from '@ag-grid-enterprise/all-modules';
import moment from 'moment';
import PropTypes from 'prop-types';

import toastr from '../toastr';
import Api from '../../services/ApiService';
import Socket from '../../services/SocketService';
import { printLogSort } from '../../actions/PrintActions';
import { getStatus } from './PrintJobUtil';
import { extractTimestamp } from '../../util/general';
import browserHistory from '../../history';
import AuthService from '../../services/AuthService';
import { legacyGetSortModel } from '../../util/grid';

const statusCellRenderer = ({ value }) => {
  const statusObj = getStatus(value);
  if (!statusObj) {
    return null;
  }

  return statusObj.icon();
};

const subscribe = () => Socket.db.emit('join', 'PrintJob');

const handleIdClick = (e, value) => {
  e.preventDefault();
  const url = `/UPD1/${value}`;
  if (e.ctrlKey) {
    window.open(url);
  } else {
    browserHistory.push(url);
  }
};

const defaultColDef = {
  enableCellChangeFlash: true,
  resizable: true,
  sortable: true,
};

class PrintLogGrid extends Component {
  constructor(props) {
    super(props);

    this.agGridContainer = React.createRef(null);

    this.state = {
      templates: null,
    };

    this.gridOptions = {
      onSortChanged: () => this.props.printLogSort(legacyGetSortModel(this.agGrid.columnApi.getColumnState())),
      rowSelection: 'multiple',
      getRowNodeId: item => item.id,
    };
  }

  componentDidMount() {
    this.sizeGrid();
    this.getTemplates();
    this.subscribe();
  }

  componentWillUnmount() {
    this.unsubscribe();
  }

  onGridReady = params => {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;

    if (this.agGridContainer.current) {
      // usually just wide enough to fit all the data, used in sizing grid
      this.initialWidth = this.agGridContainer.current.getElementsByClassName('ag-center-cols-clipper')[0].offsetWidth;
      this.sizeGrid();
    }
  };

  setRowData(data) {
    if (!this.gridApi) {
      // grid isn't ready
      setTimeout(() => this.setRowData(data), 500);
    } else {
      this.gridApi.setRowData(data);
    }
  }

  getColumnDefs() {
    // set width of status cols
    const statusWidth = 60;

    const columnDefs = [
      {
        headerClass: 'text-center',
        width: 40,
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMenu: true,
        cellStyle: { textAlign: 'center' },
        pinned: 'left',
      },
      {
        headerName: '',
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        field: 'overallStatus',
        cellRenderer: statusCellRenderer,
      },
      {
        headerName: 'ID',
        field: 'id',
        sort: 'desc',
        suppressMenu: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        cellRenderer(props) {
          const { value } = props; // eslint-disable-line
          return (
            <a onClick={e => handleIdClick(e, value)} href={`/UPD1/${value}`}>
              {value}
            </a>
          );
        },
      },
      {
        headerName: 'Created',
        field: 'id',
        suppressMenu: true,
        suppressColumnsToolPanel: true,
        suppressFiltersToolPanel: true,
        cellRenderer: ({ value }) => {
          if (!value) {
            return null;
          }
          return moment(extractTimestamp(value)).format('MMMM Do YYYY, h:mm a');
        },
      },
      {
        headerName: 'Template',
        field: 'templateId',
        cellRenderer: ({ value }) => _.get(this.state.templates[value], 'name', null),
      },
      {
        headerName: 'Description',
        field: 'description',
        sortable: false,
      },
      {
        headerName: 'Size',
        field: 'payloadSize',
        width: statusWidth,
        cellRenderer: ({ value }) => {
          if (!value) {
            return null;
          }
          return `${parseInt(value / 1000)}KB`;
        },
        suppressMenu: true,
      },
      {
        headerClass: 'text-center',
        headerComponentFramework: () => <i className="fad fa-print" title="Print" />,
        field: 'printStatus',
        cellRenderer: statusCellRenderer,
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        suppressMenu: true,
      },
      {
        headerClass: 'text-center',
        headerComponentFramework: () => <i className="fad fa-tv" title="Screen" />,
        field: 'screenStatus',
        cellRenderer: statusCellRenderer,
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        suppressMenu: true,
      },
      {
        headerClass: 'text-center',
        headerComponentFramework: () => <i className="fad fa-envelope" title="Email" />,
        field: 'emailStatus',
        cellRenderer: statusCellRenderer,
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        suppressMenu: true,
      },
      {
        headerClass: 'text-center',
        headerComponentFramework: () => <i className="fad fa-archive" title="Archive" />,
        field: 'archiveStatus',
        cellRenderer: statusCellRenderer,
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        suppressMenu: true,
      },
      {
        headerClass: 'text-center',
        headerComponentFramework: () => <i className="fad fa-paper-plane" title="FTP" />,
        field: 'ftpStatus',
        cellRenderer: statusCellRenderer,
        width: statusWidth,
        cellStyle: { textAlign: 'center' },
        suppressMenu: true,
      },
    ];

    return columnDefs;
  }

  getContextMenuItems = (params = {}) => {
    const menu = [];
    const jobId = params.node.data.id;

    menu.push({
      name: 'Job Details',
      icon: '<i class="fad fa-external-link" />',
      action: () => {
        window.open(`/UPD1/${jobId}`);
      },
    });

    menu.push('separator');

    menu.push({
      name: 'Resubmit',
      icon: '<i class="fad fa-sync" />',
      action: () => {
        toastr.info(`Resubmitting Job: ${jobId}`);
        Api.post('print/jobs/resubmit', { ids: [jobId] });
      },
    });

    menu.push({
      name: 'Retry Failed',
      icon: '<i class="fad fa-exclamation-triangle" />',
      action: () => {
        toastr.info(`Retrying Job: ${jobId}`);
        Api.post('print/jobs/retry', { ids: [jobId] });
      },
    });

    menu.push({
      name: 'Cancel',
      icon: '<i class="fas fa-stop" />',
      action: () => {
        toastr.info(`Canceling Job: ${jobId}`);
        Api.post('print/jobs/cancel', { ids: [jobId] });
      },
    });

    menu.push('separator');
    menu.push('copy', 'copyWithHeaders');
    return menu;
  };

  getTemplates() {
    Api.get('print/templates').then(({ data }) => {
      this.setState({ templates: _.keyBy(data, 'id') });
    });
  }

  subscribe() {
    const shouldHideJob = job => {
      // do not add hidden jobs for regular users
      return !AuthService.isDsiSupport() && _.get(job, 'meta.hidden');
    };

    subscribe();
    Socket.db.on('connect', subscribe);
    Socket.db.on('PrintJob.create', res => {
      if (shouldHideJob(res.data)) {
        return;
      }

      if (this.gridOptions.api) {
        this.gridOptions.api.updateRowData({ add: [res.data] });
      }
    });

    Socket.db.on('PrintJob.update', res => {
      if (shouldHideJob(res.data)) {
        return;
      }
      this.updateRowById(res.data, res.id);
    });
  }

  unsubscribe() {
    Socket.db.off('connect', subscribe);
    ['create', 'update'].forEach(action => {
      Socket.db.off(`PrintJob.${action}`);
    });

    Socket.db.emit('leave', 'PrintJob');
  }

  findRowById(id) {
    let foundNode = null;
    if (!this.gridApi) {
      return null;
    }
    this.gridApi.forEachNode(node => {
      if (_.get(node, 'data.id') === id) {
        foundNode = node;
      }
    });

    return foundNode;
  }

  updateRowById(data, id) {
    const node = this.findRowById(id);
    if (!node || !node.data) {
      return;
    }

    _.assign(node.data, data);

    this.gridApi.refreshCells({ rowNodes: [node] });
  }

  sizeGrid = () => {
    if (!this.agGridContainer.current || !this.agGrid) {
      return;
    }

    if (
      this.agGridContainer.current.offsetWidth >
      (this.initialWidth ||
        this.agGridContainer.current.getElementsByClassName('ag-center-cols-clipper')[0].offsetWidth)
    ) {
      this.agGrid.api.sizeColumnsToFit();
    }
  };

  render() {
    if (!this.state.templates) {
      return null;
    }

    return (
      <div id="grid-wrapper" ref={this.agGridContainer} className="ag-theme-balham">
        <div style={{ width: '100%', display: 'block', height: '100%', minHeight: '300px' }}>
          <AgGridReact
            ref={el => {
              this.agGrid = el;
            }}
            deltaRowDataMode
            gridOptions={this.gridOptions}
            onGridReady={this.onGridReady}
            onGridSizeChanged={this.sizeGrid}
            columnDefs={this.getColumnDefs()}
            defaultColDef={defaultColDef}
            getContextMenuItems={this.getContextMenuItems}
            modules={AllModules}
            suppressCellFocus
            suppressPropertyNamesCheck
          />
        </div>
      </div>
    );
  }
}

PrintLogGrid.propTypes = {
  printLogSort: PropTypes.func,
};

const mapDispatchToProps = dispatch => {
  return bindActionCreators({ printLogSort }, dispatch);
};

export default connect(null, mapDispatchToProps, null, { forwardRef: true })(PrintLogGrid);
